import { useEffect, useRef } from "react";
import { apiClient, useAuth } from "../auth";
import { useI18n } from "../i18n";
import { toast } from "sonner";

const STORAGE_KEY = "paw_badges_seen_v1";

function readSeen() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null; // null means "first visit, don't celebrate retroactively"
    return new Set(JSON.parse(raw));
  } catch {
    return null;
  }
}

function writeSeen(set) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...set]));
  } catch {
    /* ignore */
  }
}

/**
 * On every route change (and on user login), fetch the current earned-badge
 * set and compare against the locally-stored "seen" set. For each *new* badge,
 * fire a celebration: toast + window event "paw-celebrate" that PawPoints can
 * listen to for its pop animation.
 *
 * First-ever load just seeds the seen-set silently (so old users don't get
 * spammed with 30+ retroactive celebrations).
 */
export function useBadgeCelebration(routeKey) {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const inFlight = useRef(false);

  useEffect(() => {
    if (!user || inFlight.current) return;
    inFlight.current = true;

    apiClient()
      .get("/users/me/badges")
      .then((r) => {
        // Defensive: production users with corrupted DB records may have
        // `badges` as an object (`{}`) or other non-array shape. The
        // `|| []` short-circuit only fires for FALSY values, so truthy
        // objects sneak through and crash `.filter` at app-root → blank
        // screen (caught by ErrorBoundary now, but still bad UX). Always
        // coerce to array via Array.isArray() guard. Real-user verification
        // (Feb 2026): one tester's profile crashed at app-root with
        // "filter is not a function" — this was the source.
        const rawBadges = r.data?.badges;
        const badgesArr = Array.isArray(rawBadges) ? rawBadges : [];
        const earned = badgesArr.filter((b) => b && b.earned);
        const earnedKeys = new Set(earned.map((b) => b.key));

        const seen = readSeen();
        if (seen === null) {
          // First visit on this device — seed silently, no celebration.
          writeSeen(earnedKeys);
          return;
        }

        const fresh = earned.filter((b) => !seen.has(b.key));
        if (fresh.length > 0) {
          fresh.forEach((b, idx) => {
            const name = b[lang === "tr" ? "name_tr" : "name_en"];
            const desc = b[lang === "tr" ? "desc_tr" : "desc_en"];
            setTimeout(() => {
              toast.success(`${b.emoji}  ${t("badges.celebrationTitle")}: ${name}`, {
                description: desc,
                duration: 5500,
                className: "paw-celebrate-toast",
              });
              window.dispatchEvent(
                new CustomEvent("paw-celebrate", { detail: { badge: b } })
              );
            }, idx * 800);
          });
          writeSeen(earnedKeys);
        } else {
          // Still update set so removed badges (rare) sync, but no celebration.
          writeSeen(earnedKeys);
        }
      })
      .catch(() => {})
      .finally(() => {
        inFlight.current = false;
      });
  }, [user, routeKey, lang, t]);
}
